import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import socketService from '../services/socketService';
import api from '../services/api';

/**
 * 🚀 HOOK CENTRAL WEBSOCKET REAL-TIME
 * 
 * Centraliza TODA comunicação WebSocket substituindo o polling excessivo.
 * 
 * ANTES: Consultas a cada 2-60s → 95% carga desnecessária no DB
 * DEPOIS: 0 consultas periódicas → Atualizações instantâneas via WebSocket
 * 
 * @param {Object} options - Configurações dos dados a serem monitorados
 * @param {boolean} options.senhas - Monitorar senhas e display (substitui useSenhasApi)
 * @param {boolean} options.fila - Monitorar fila de senhas (substitui buscarSenhas)
 * @param {boolean} options.estatisticas - Monitorar estatísticas (substitui buscarEstatisticas) 
 * @param {boolean} options.usuario - Monitorar dados do usuário (substitui consultas de usuário)
 * @param {boolean} options.historico - Monitorar histórico de senhas atendidas
 * @param {number[]} options.servicosIds - IDs dos serviços para filtrar (opcional)
 */
const useWebSocketData = (options = {}) => {
  const { user } = useAuth();
  
  // ==========================================
  // CONFIGURAÇÕES E ESTADOS PRINCIPAIS
  // ==========================================
  
  const {
    senhas = false,
    fila = false,
    estatisticas = false,
    usuario = false,
    historico = false,
    servicosIds = null
  } = options;

  // Estados para dados de senhas (substituir useSenhasApi)
  const [senhaAtual, setSenhaAtual] = useState(null);
  const [guicheAtual, setGuicheAtual] = useState(null);
  const [preferencialAtual, setPreferencialAtual] = useState(false);
  const [ultimasSenhas, setUltimasSenhas] = useState([]);
  const [dataChamadaAtual, setDataChamadaAtual] = useState(null);

  // Estados para fila de senhas (substituir buscarSenhas) 
  const [proximasSenhas, setProximasSenhas] = useState([]);
  const [filaSenhas, setFilaSenhas] = useState([]);
  const [senhaChamada, setSenhaChamada] = useState(null);

  // Estados para estatísticas (substituir buscarEstatisticas)
  const [estatisticasGerais, setEstatisticasGerais] = useState({
    senhasAtivas: 0,
    tempoMedioAtendimento: '0 min',
    naoComparecimentos: 0,
    atendimentosHoje: 0
  });

  // Estados para histórico (substituir HistoricoSenhas)
  const [senhasAtendidas, setSenhasAtendidas] = useState([]);
  const [senhasFinalizadas, setSenhasFinalizadas] = useState([]);

  // Estados para dados do usuário
  const [dadosUsuario, setDadosUsuario] = useState(null);
  const [statusSessao, setStatusSessao] = useState('OFFLINE');

  // Estados de controle
  const [conectado, setConectado] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);

  // Referências para controle
  const listenersRef = useRef(new Set());
  const subscricoesAtivasRef = useRef(new Set());
  const fallbackTimeoutRef = useRef(null);

  // ==========================================
  // FUNÇÕES AUXILIARES
  // ==========================================

  // Função para calcular tempo de espera
  const calcularTempoEspera = useCallback((dataEmissao) => {
    const agora = new Date();
    const emissao = new Date(dataEmissao);
    const diferencaMinutos = Math.floor((agora - emissao) / (1000 * 60));
    return `${diferencaMinutos} min`;
  }, []);

  // Função para formatar horário
  const formatarHorario = useCallback((dataHora) => {
    if (!dataHora) return '';
    try {
      const data = new Date(dataHora);
      return data.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
    } catch (error) {
      console.error('❌ Erro ao formatar horário:', error);
      return '';
    }
  }, []);

  // ==========================================
  // HANDLERS DOS EVENTOS WEBSOCKET
  // ==========================================

  // Handler para atualização do display (substitui useSenhasApi.fetchDados)
  const handleDisplayAtualizado = useCallback((data) => {
    console.log('📱 WebSocket: Display atualizado', data);
    
    if (data.senhaAtual) {
      setSenhaAtual(data.senhaAtual.numero);
      setGuicheAtual(data.senhaAtual.guiche);
      setPreferencialAtual(data.senhaAtual.preferencial);
      setDataChamadaAtual(formatarHorario(data.senhaAtual.chamada_em));
    } else {
      setSenhaAtual(null);
      setGuicheAtual(null);
      setPreferencialAtual(false);
      setDataChamadaAtual(null);
    }

    if (data.ultimasSenhas) {
      setUltimasSenhas(data.ultimasSenhas.map(senha => ({
        senha: senha.numero,
        guiche: senha.guiche,
        preferencial: senha.preferencial,
        horario: formatarHorario(senha.horario),
        status: senha.status || 'CHAMADA'
      })));
    }

    setUltimaAtualizacao(new Date());
  }, [formatarHorario]);

  // Handler para fila atualizada (substitui buscarSenhas)
  const handleFilaAtualizada = useCallback((data) => {
    console.log('📋 WebSocket: Fila atualizada', data);
    
    if (data.senhas) {
      const senhasMapeadas = data.senhas.map(senha => ({
        id: senha.id,
        numero: senha.numero,
        tipo: senha.tipo,
        servicoId: senha.servicoId,
        status: senha.status,
        tempEspera: senha.tempEspera || calcularTempoEspera(senha.emitida_em),
        progresso: 0,
        preferencial: senha.preferencial,
        emitida_em: senha.emitida_em
      }));
      
      setFilaSenhas(senhasMapeadas);
    }

    if (data.estatisticas) {
      setEstatisticasGerais(prev => ({
        ...prev,
        senhasAtivas: data.estatisticas.totalAguardando
      }));
    }

    setUltimaAtualizacao(new Date());
  }, [calcularTempoEspera]);

  // Handler para próximas senhas do atendente
  const handleProximasSenhasChanged = useCallback((data) => {
    console.log('🎯 WebSocket: Próximas senhas do atendente atualizadas', data);
    
    if (data.atendenteId === user?.id && data.proximasSenhas) {
      setProximasSenhas(data.proximasSenhas);
    }
    
    setUltimaAtualizacao(new Date());
  }, [user?.id]);

  // Handler para estatísticas atualizadas (substitui buscarEstatisticas)
  const handleEstatisticasAtualizadas = useCallback((data) => {
    console.log('📊 WebSocket: Estatísticas atualizadas', data);
    
    if (data.estatisticas) {
      setEstatisticasGerais(data.estatisticas);
    }
    
    setUltimaAtualizacao(new Date());
  }, []);

  // Handler para senha emitida
  const handleSenhaEmitida = useCallback((data) => {
    console.log('🆕 WebSocket: Nova senha emitida', data);
    
    // Atualizar estatísticas se disponíveis
    if (data.estatisticas) {
      setEstatisticasGerais(prev => ({
        ...prev,
        ...data.estatisticas
      }));
    }
    
    setUltimaAtualizacao(new Date());
    
    // CORREÇÃO: Remover notificação duplicada - já está sendo tratada no socketService
    // As notificações agora são centralizadas no socketService via notificationManager
  }, [senhas]);

  // Handler para mudança de status de senha
  const handleSenhaStatusChanged = useCallback((data) => {
    console.log('🔄 WebSocket: Status da senha alterado', data);
    
    // Se é a senha atual do atendente
    if (senhaChamada && senhaChamada.id === data.senhaId) {
      setSenhaChamada(prev => ({
        ...prev,
        status: data.novoStatus,
        ...data.senha
      }));
    }
    
    setUltimaAtualizacao(new Date());
  }, [senhaChamada]);

  // Handler para senha finalizada (para histórico)
  const handleSenhaFinalizada = useCallback((data) => {
    console.log('✅ WebSocket: Senha finalizada', data);
    
    // Adicionar ao histórico de senhas finalizadas
    const senhaFinalizada = {
      id: data.senhaId,
      senha: data.senha,
      guiche: data.guiche,
      atendenteId: data.atendenteId,
      tempoAtendimento: data.tempoAtendimento,
      finalizada_em: data.finalizada_em,
      horario: formatarHorario(data.finalizada_em)
    };
    
    setSenhasFinalizadas(prev => [senhaFinalizada, ...prev.slice(0, 49)]); // Manter apenas 50
    setSenhasAtendidas(prev => [senhaFinalizada, ...prev.slice(0, 49)]);
    
    // Atualizar estatísticas se disponíveis
    if (data.estatisticas) {
      setEstatisticasGerais(prev => ({
        ...prev,
        ...data.estatisticas
      }));
    }
    
    setUltimaAtualizacao(new Date());
  }, [formatarHorario]);

  // Handler para usuário atualizado
  const handleUsuarioAtualizado = useCallback((data) => {
    console.log('👤 WebSocket: Usuário atualizado', data);
    
    if (data.usuarioId === user?.id) {
      setDadosUsuario(prev => ({
        ...prev,
        ...data.alteracoes
      }));
    }
    
    setUltimaAtualizacao(new Date());
  }, [user?.id]);

  // Handler para status da sessão
  const handleSessionStatus = useCallback((data) => {
    console.log('🔗 WebSocket: Status da sessão atualizado', data);
    
    if (data.usuarioId === user?.id) {
      setStatusSessao(data.status);
      
      if (data.senhaEmAtendimento) {
        setSenhaChamada(data.senhaEmAtendimento);
      }
    }
    
    setUltimaAtualizacao(new Date());
  }, [user?.id]);

  // ==========================================
  // SISTEMA DE FALLBACK HTTP
  // ==========================================

  // Fallback para buscar dados via HTTP se WebSocket falhar
  const executarFallbackHTTP = useCallback(async () => {
    console.log('🔄 Executando fallback HTTP...');
    
    try {
      const promises = [];
      
      // Fallback para senhas (substitui useSenhasApi)
      if (senhas) {
        promises.push(
          api.get('/senhas/chamadas').then(response => {
            if (response.data.success && response.data.data.length > 0) {
              const senhaAtualData = response.data.data[0];
              setSenhaAtual(senhaAtualData.senha);
              setGuicheAtual(senhaAtualData.guiche);
              setPreferencialAtual(senhaAtualData.senha.startsWith('P'));
              setDataChamadaAtual(formatarHorario(senhaAtualData.chamada_em));
              
              const ultimasSenhasData = response.data.data.slice(1, 5);
              setUltimasSenhas(ultimasSenhasData.map(senha => ({
                senha: senha.senha,
                guiche: senha.guiche,
                preferencial: senha.preferencial,
                horario: formatarHorario(senha.chamada_em),
                status: senha.status
              })));
            }
          })
        );
      }
      
      // Fallback para fila (substitui buscarSenhas)
      if (fila) {
        promises.push(
          api.get('/senhas/aguardando').then(response => {
            if (response.data.success) {
              const senhasMapeadas = response.data.data.map(senha => ({
                id: senha.id,
                numero: senha.senha,
                tipo: senha.servico_nome,
                status: senha.status,
                tempEspera: calcularTempoEspera(senha.emitida_em),
                progresso: 0,
                preferencial: senha.preferencial,
                emitida_em: senha.emitida_em
              }));
              setFilaSenhas(senhasMapeadas);
            }
          })
        );
      }
      
      // Fallback para estatísticas (substitui buscarEstatisticas)
      if (estatisticas) {
        promises.push(
          api.get('/senhas/estatisticas').then(response => {
            if (response.data.success) {
              setEstatisticasGerais(response.data.data);
            }
          })
        );
      }
      
      await Promise.allSettled(promises);
      console.log('✅ Fallback HTTP concluído');
      
    } catch (error) {
      console.error('❌ Erro no fallback HTTP:', error);
      setErro(`Erro de conexão: ${error.message}`);
    }
  }, [senhas, fila, estatisticas, calcularTempoEspera, formatarHorario]);

  // ==========================================
  // GERENCIAMENTO DE CONEXÃO WEBSOCKET
  // ==========================================

  // Configurar listeners WebSocket
  const configurarListeners = useCallback(() => {
    console.log('🔧 Configurando listeners WebSocket...');
    
    // Listeners de conexão
    socketService.on('connect', () => {
      console.log('✅ WebSocket conectado');
      setConectado(true);
      setErro(null);
      
      // Cancelar fallback se estiver rodando
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
    });

    socketService.on('disconnect', () => {
      console.log('❌ WebSocket desconectado');
      setConectado(false);
      
      // Configurar fallback após 5 segundos de desconexão
      fallbackTimeoutRef.current = setTimeout(() => {
        executarFallbackHTTP();
      }, 5000);
    });

    // Listeners para dados (baseados nas opções)
    if (senhas) {
      socketService.on('displayAtualizado', handleDisplayAtualizado);
      listenersRef.current.add('displayAtualizado');
    }

    if (fila) {
      socketService.on('filaAtualizada', handleFilaAtualizada);
      socketService.on('proximasSenhasChanged', handleProximasSenhasChanged);
      listenersRef.current.add('filaAtualizada');
      listenersRef.current.add('proximasSenhasChanged');
    }

    if (estatisticas) {
      socketService.on('estatisticasAtualizadas', handleEstatisticasAtualizadas);
      listenersRef.current.add('estatisticasAtualizadas');
    }

    if (historico) {
      socketService.on('senhaFinalizada', handleSenhaFinalizada);
      listenersRef.current.add('senhaFinalizada');
    }

    if (usuario) {
      socketService.on('usuarioAtualizado', handleUsuarioAtualizado);
      socketService.on('sessionStatus', handleSessionStatus);
      listenersRef.current.add('usuarioAtualizado');
      listenersRef.current.add('sessionStatus');
    }

    // Listeners para mudanças gerais
    socketService.on('senhaEmitida', handleSenhaEmitida);
    socketService.on('senhaStatusChanged', handleSenhaStatusChanged);
    listenersRef.current.add('senhaEmitida');
    listenersRef.current.add('senhaStatusChanged');

  }, [
    senhas, fila, estatisticas, historico, usuario,
    handleDisplayAtualizado, handleFilaAtualizada, handleProximasSenhasChanged,
    handleEstatisticasAtualizadas, handleSenhaFinalizada, handleUsuarioAtualizado,
    handleSessionStatus, handleSenhaEmitida, handleSenhaStatusChanged,
    executarFallbackHTTP
  ]);

  // Remover listeners WebSocket
  const removerListeners = useCallback(() => {
    console.log('🧹 Removendo listeners WebSocket...');
    
    listenersRef.current.forEach(evento => {
      socketService.off(evento);
    });
    
    listenersRef.current.clear();
  }, []);

  // Fazer subscrições necessárias
  const fazerSubscricoes = useCallback(() => {
    if (!user?.id || !conectado) return;

    console.log('📡 Fazendo subscrições WebSocket...');

    // Subscrever para dados do usuário
    if (usuario) {
      socketService.emit('subscribeToUser', { usuarioId: user.id });
      subscricoesAtivasRef.current.add('subscribeToUser');
    }

    // Subscrever para serviços específicos
    if (fila && servicosIds) {
      socketService.emit('subscribeToServicos', { 
        atendenteId: user.id, 
        servicosIds 
      });
      subscricoesAtivasRef.current.add('subscribeToServicos');
    }

  }, [user?.id, conectado, usuario, fila, servicosIds]);

  // ==========================================
  // EFEITOS PRINCIPAIS
  // ==========================================

  // Efeito para inicializar WebSocket
  useEffect(() => {
    console.log('🚀 Inicializando useWebSocketData...');
    
    // Conectar se não estiver conectado
    if (!socketService.socket) {
      socketService.connect();
    }
    
    // Configurar listeners
    configurarListeners();
    
    // Buscar dados iniciais via HTTP (não polling!)
    executarFallbackHTTP().finally(() => {
      setCarregando(false);
    });

    return () => {
      removerListeners();
      
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
    };
  }, [configurarListeners, removerListeners, executarFallbackHTTP]);

  // Efeito para fazer subscrições quando conectar
  useEffect(() => {
    if (conectado && user?.id) {
      fazerSubscricoes();
    }
  }, [conectado, user?.id, fazerSubscricoes]);

  // ==========================================
  // API PÚBLICA DO HOOK
  // ==========================================

  // Funções para emitir eventos (substitui chamadas de API)
  const emitirEventos = useMemo(() => ({
    // Substituir chamadas diretas de chamarSenha
    chamarSenha: (senhaId, atendenteId, guiche) => {
      socketService.emit('chamarSenha', { id: senhaId, atendenteId, guiche });
    },
    
    // Substituir chamadas de iniciarAtendimento
    iniciarAtendimento: (senhaId, atendenteId, guiche) => {
      socketService.emit('iniciarAtendimento', { senhaId, atendenteId, guiche });
    },
    
    // Substituir chamadas de finalizarAtendimento
    finalizarAtendimento: (senhaId, atendenteId, observacoes) => {
      socketService.emit('finalizarAtendimento', { senhaId, atendenteId, observacoes });
    },
    
    // Substituir chamadas de cancelarSenha
    cancelarSenha: (senhaId, atendenteId, motivo) => {
      socketService.emit('cancelarSenha', { senhaId, atendenteId, motivo });
    },
    
    // Rechamar senha
    rechamarSenha: (senha, guiche, preferencial, vocalizarSenha) => {
      socketService.emit('rechamarSenha', { senha, guiche, preferencial, vocalizarSenha, timestamp: Date.now() });
    }
  }), []);

  // Estado calculado
  const estado = useMemo(() => ({
    // Estados de dados (substitui retornos dos hooks de polling)
    senhaAtual,
    guicheAtual, 
    preferencialAtual,
    dataChamadaAtual,
    ultimasSenhas,
    proximasSenhas,
    filaSenhas,
    senhaChamada,
    estatisticasGerais,
    senhasAtendidas,
    senhasFinalizadas,
    dadosUsuario,
    statusSessao,
    
    // Estados de controle
    conectado,
    carregando,
    erro,
    ultimaAtualizacao,
    
    // Funções de ação
    ...emitirEventos,
    
    // Funções auxiliares
    calcularTempoEspera,
    formatarHorario,
    executarFallbackHTTP
  }), [
    senhaAtual, guicheAtual, preferencialAtual, dataChamadaAtual, ultimasSenhas,
    proximasSenhas, filaSenhas, senhaChamada, estatisticasGerais, senhasAtendidas,
    senhasFinalizadas, dadosUsuario, statusSessao, conectado, carregando, erro,
    ultimaAtualizacao, emitirEventos, calcularTempoEspera, formatarHorario, executarFallbackHTTP
  ]);

  return estado;
};

export default useWebSocketData; 